import { useEffect, useMemo, useRef, useState } from "react";
import { atom, useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { getDefaultStore } from "jotai/vanilla";
import { last } from "lodash";
import { useSignal } from "@greeter/event";
import { differenceInMilliseconds } from "date-fns";

type Milliseconds = number;

export type QueryablePromiseOptions = {
  timeout?: Milliseconds;
};
export class QueryablePromise<T> {
  _isSuccess: boolean = false;
  _isError: boolean = false;
  _isSettled: boolean = false;
  _startedAt: Date = new Date();
  _timeout: Milliseconds = 10_000;

  constructor(public promise: Promise<T>, opts?: QueryablePromiseOptions) {
    this._startedAt = new Date();
    if (opts?.timeout) this._timeout = opts.timeout;

    promise
      .then(() => {
        this._isSuccess = true;
        this._isSettled = true;
        this._isError = false;
      })
      .catch(() => {
        this._isError = true;
        this._isSettled = true;
        this._isSuccess = false;
      });
  }

  get isSuccess(): boolean {
    return this._isSuccess;
  }

  get isSettled(): boolean {
    return this._isSettled;
  }

  get isError(): boolean {
    return this._isError;
  }

  get isTimedOut(): boolean {
    const diffInMs = differenceInMilliseconds(new Date(), this._startedAt);
    const timedout = diffInMs > this._timeout;
    return timedout;
  }
}

/**
 * Used as an alternative to the redirectUrl stacking which didnt work too well
 */
export const redirectUrlsAtom = atomWithStorage("redirectUrls", []);
export const useRedirectUrlsAtom = () => useAtom(redirectUrlsAtom);

export const isLoadingAtom = atom(false);
export const reasonAtom = atom("");

export type WaitForPromise<T> = {
  promise: QueryablePromise<T>;
  reason: string;
};
export const waitingForTasksAtom = atom<WaitForPromise<any>[]>(
  [] as WaitForPromise<any>[]
);

function filterChangedTasks(v: WaitForPromise<any>[]) {
  const n = v.filter((p) => !p.promise.isSettled && !p.promise.isTimedOut);
  if (n.length !== v.length) {
    return n;
  } else {
    for (let i = 0; i < n.length; i++) {
      const a = n[i];
      const b = v[i];
      if (a !== b) return n;
    }
  }
  return v;
}

waitingForTasksAtom.onMount = (set) => {
  setInterval(() => set(filterChangedTasks), 1000);
};

export function useLoading() {
  const [tasks, setTasks] = useAtom(waitingForTasksAtom);

  function pushTask<T>(task: WaitForPromise<T>) {
    setTasks((p) => [...p, task]);
  }

  function waitFor<T>(
    task: Promise<T>,
    reason: string = "",
    opts?: QueryablePromiseOptions
  ) {
    const qp = new QueryablePromise(task, opts);
    const t: WaitForPromise<T> = {
      promise: qp,
      reason: reason,
    };
    pushTask(t);
    qp.promise.then(() => setTasks(filterChangedTasks));
    return task;
  }

  const numberOfTasks = tasks.length;
  const reason = last(tasks)?.reason ?? "";
  const result = {
    waitFor,
    isLoading: numberOfTasks > 0,
    reason: reason,
    numberOfTasks: numberOfTasks,
  };
  return result;
}

/**
 * @deprecated should use Spinner instead even though it's cool to use Signals
 */
export const useDeclarativeLoading = (loading: boolean, msg: string) => {
  const doneSignal = useSignal();
  const { waitFor } = useLoading();

  return useEffect(() => {
    if (loading) {
      waitFor(
        new Promise<void>((resolve) => {
          doneSignal.subscribe(() => resolve());
        }),
        msg
      );
    } else {
      doneSignal.emit();
    }

    return () => {
      doneSignal.emit();
    };
  }, [waitFor, doneSignal, loading, msg]);
};
